import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { matchesSideNorm } from "./orderFilterHelpers";
import AuthService from "../../../api/services/AuthService";
import {
  decNum,
  decStr,
  fmtTs,
  formatOptionsOpenOrderPrice,
  formatOptionsOrderHistoryAvgFill,
  formatOptionsOrderHistorySide,
  formatOptionsOrderStatus,
  formatOptionsOrderType,
  isReduceOnlyIntent,
  optionsOpenOrderAvgFill,
} from "../options/optionsDataHelpers";

const ORDER_DESKTOP_COL_COUNT = 13;

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btc_usdt", label: "BTC/USDT" },
  { value: "eth_usdt", label: "ETH/USDT" },
  { value: "bnb_usdt", label: "BNB/USDT" },
];

const SIDE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
];

function extractOrderHistory(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d?.orders)) return d.orders;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d)) return d;
  if (Array.isArray(res.orders)) return res.orders;
  if (Array.isArray(res.items)) return res.items;
  return [];
}

function matchesOptionsContractFilter(symbol, filter) {
  if (!filter || filter === "all") return true;
  const sym = String(symbol || "").toUpperCase();
  const fc = String(filter).replace(/[/_]/g, "").toUpperCase();
  if (!sym || !fc) return false;
  if (sym === fc) return true;
  const base = fc.replace(/USDT$|USDC$|BUSD$/, "");
  return Boolean(base) && (sym.startsWith(`${base}-`) || sym.startsWith(fc));
}

function formatQty(v) {
  const n = decNum(v);
  if (!(n > 0) && n !== 0) return "—";
  return decStr(v) || String(n);
}

function filledPct(item) {
  const filled = decNum(item?.filled_quantity);
  const qty = decNum(item?.quantity);
  if (!(qty > 0)) return "—";
  return `${Math.min(100, Math.round((filled / qty) * 100))}%`;
}

function formatFee(item) {
  const v = item?.total_fees ?? item?.total_fees_paid ?? item?.fee ?? item?.trading_fee;
  const n = decNum(v);
  if (!(n > 0) && n !== 0) return "—";
  const asset = item?.fee_asset || "USDT";
  return `${decStr(v) || n} ${asset}`;
}

function mapOrderHistoryRow(item, idx) {
  const sideFmt = formatOptionsOrderHistorySide(item?.side);
  const avg = formatOptionsOrderHistoryAvgFill(item);
  const id = item?.order_id || item?._id || item?.client_order_id || `opt-hist-${idx}`;
  return {
    id,
    contract: item?.symbol || item?.contract || item?.instrument || "—",
    creationTime: fmtTs(item?.created_at),
    endTime: fmtTs(item?.updated_at || item?.filled_at || item?.cancelled_at),
    side: sideFmt.label,
    size: formatQty(item?.quantity),
    avgFillPrice: avg !== "—" ? avg : optionsOpenOrderAvgFill(item),
    orderPrice: formatOptionsOpenOrderPrice(item),
    filled: filledPct(item),
    fee: formatFee(item),
    executionType: formatOptionsOrderType(item?.order_type),
    reduceOnly: isReduceOnlyIntent(item?.order_intent) ? "Yes" : "No",
    orderId: item?.client_order_id || item?.order_id || item?._id || "—",
    status: formatOptionsOrderStatus(item?.status),
  };
}

const OptionsOrderHistory = () => {
  const [contract, setContract] = useState("all");
  const [side, setSide] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [rows, setRows] = useState([]);

  const loadOrders = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setRows([]);
      return;
    }
    try {
      const res = await AuthService.optionsOrderHistory({
        skip: 0,
        limit: 100,
      });
      setRows(extractOrderHistory(res).map(mapOrderHistoryRow));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = useMemo(() => {
    return rows.filter(
      (r) => matchesOptionsContractFilter(r.contract, contract) && matchesSideNorm(r.side, side)
    );
  }, [rows, contract, side]);

  const resetFilters = () => {
    setContract("all");
    setSide("all");
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter options order history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}ooh-dd-contract`}
          label="Contract"
          value={contract}
          options={CONTRACT_OPTIONS}
          onChange={setContract}
          menuKey={`${pref}ooh-contract`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}ooh-dd-side`}
          label="Side"
          value={side}
          options={SIDE_OPTIONS}
          onChange={setSide}
          menuKey={`${pref}ooh-side`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={13} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap so-optionsOrderHistoryPage">
          <main className="so-page" aria-labelledby="so-options-order-history-title">
            <h1 id="so-options-order-history-title" className="so-page__title">
              Order History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
                { label: "Order History", to: "/user_profile/orders/options_order_history" },
                { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
                { label: "Position History", to: "/user_profile/orders/options_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking so-optionsOrderHistoryTableShell">
              <table className="so-table so-table--rowLines so-table--air so-table--optionsOrderHistory">
                <colgroup>
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--contract" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--created" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--end" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--side" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--size" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--avg" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--orderPx" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--filled" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--fee" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--exec" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--reduce" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--orderId" />
                  <col className="so-optionsOrderHistory__cw so-optionsOrderHistory__cw--status" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Creation Time</th>
                    <th scope="col">End Time</th>
                    <th scope="col">Side</th>
                    <th scope="col">Size</th>
                    <th scope="col">Avg. Fill Price</th>
                    <th scope="col">Order Price</th>
                    <th scope="col">Filled</th>
                    <th scope="col">Fee</th>
                    <th scope="col">Exec. Type</th>
                    <th scope="col">Reduce-Only</th>
                    <th scope="col">Order ID</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : filteredRows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong className="so-optionsOrderHistory__strong">{r.contract}</strong>
                          </td>
                          <td className="so-num so-optionsOrderHistory__cellTime">{r.creationTime}</td>
                          <td className="so-num so-optionsOrderHistory__cellTime">{r.endTime}</td>
                          <td className="so-optionsOrderHistory__sideCell">{r.side || "—"}</td>
                          <td className="so-num">{r.size}</td>
                          <td className="so-num">{r.avgFillPrice}</td>
                          <td className="so-num">{r.orderPrice}</td>
                          <td className="so-num">{r.filled}</td>
                          <td className="so-num">{r.fee}</td>
                          <td>{r.executionType}</td>
                          <td className="so-optionsOrderHistory__tdReduce">{r.reduceOnly}</td>
                          <td className="so-optionsOrderHistory__cellClip so-num">{r.orderId}</td>
                          <td>
                            <span className="so-pnl--profit">{r.status}</span>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Options Order History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/options_open_orders" },
              { label: "Order History", to: "/user_profile/orders/options_order_history" },
              { label: "Trade History", to: "/user_profile/orders/options_trade_history" },
              { label: "Position History", to: "/user_profile/orders/options_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-options-order-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            filteredRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Options order details">
                <div className="so-mobileCard__row">
                  <span>Contract</span>
                  <strong className="so-optionsOrderHistory__strong">{r.contract}</strong>
                </div>
                <div className="so-mobileCard__row">
                  <span>Creation Time</span>
                  <span className="so-num">{r.creationTime}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>End Time</span>
                  <span className="so-num">{r.endTime}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Side</span>
                  <span>{r.side || "—"}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Size</span>
                  <span className="so-num">{r.size}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Avg. Fill Price</span>
                  <span className="so-num">{r.avgFillPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order Price</span>
                  <span className="so-num">{r.orderPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Filled</span>
                  <span className="so-num">{r.filled}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fee</span>
                  <span className="so-num">{r.fee}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Execution Type</span>
                  <span>{r.executionType}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Reduce-Only</span>
                  <span>{r.reduceOnly}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order ID</span>
                  <span className="so-num">{r.orderId}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span className="so-pnl--profit">{r.status}</span>
                </div>
              </OrderMobileCardShell>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OptionsOrderHistory;
